import { useMutation, useQuery } from '@tanstack/react-query'
import {
  createManyQuestions,
  createOrFetchEnterpriseSurvey,
  createSurvey,
  deleteOneQuestion,
  fetchSurveyLinks,
  fetchEnterpriseSurveys,
  fetchOneSurvey,
  fetchSurveys,
  patchAnonymousSurveyAccount,
  patchOneQuestion,
  patchSurvey,
  fetchSurveyShortlink,
  deleteSurveyLink,
  moveSurveyPage,
  duplicateSurveyPage,
  deleteSurveyPage
} from './api'
import { MutationConfig, MutationConfigPickRes, QueryConfig, QueryConfigPickRes } from 'api/reactQuery'

export const surveysQueryKeys = {
  getMany: (queryParams?: Parameters<typeof fetchSurveys>[0]) =>
    ['surveys', ...(queryParams ? [queryParams] : [])] as const,
  getOne: (surveyId: string | undefined | null, queryParams?: Parameters<typeof fetchOneSurvey>[1]) =>
    ['surveys', surveyId, ...(queryParams ? [queryParams] : [])] as const
}

export const useCreateOrFetchEnterpriseSurvey = (
  body: Parameters<typeof createOrFetchEnterpriseSurvey>[0],
  queryParams: Parameters<typeof createOrFetchEnterpriseSurvey>[1] = {},
  options?: QueryConfigPickRes<typeof createOrFetchEnterpriseSurvey, 'data'>
) =>
  useQuery({
    queryKey: ['enterprises-surveys', body, queryParams],
    queryFn: async () => {
      const { data } = await createOrFetchEnterpriseSurvey(body, queryParams)
      return data
    },
    ...options
  })

export const useCreateOrFetchEnterpriseSurveyMutation = (
  queryParams: Parameters<typeof createOrFetchEnterpriseSurvey>[1] = {},
  options?: MutationConfigPickRes<
    typeof createOrFetchEnterpriseSurvey,
    'data',
    Parameters<typeof createOrFetchEnterpriseSurvey>[0]
  >
) =>
  useMutation({
    mutationFn: async (body) => {
      const { data } = await createOrFetchEnterpriseSurvey(body, queryParams)
      return data
    },
    ...options
  })

export const useSurveyLinks = (
  queryParams: Parameters<typeof fetchSurveyLinks>[0] = {},
  options: QueryConfig<typeof fetchSurveyLinks> = {}
) =>
  useQuery({
    queryKey: ['enterprises-surveys', queryParams],
    queryFn: () => fetchSurveyLinks(queryParams),
    ...options
  })

export const useSurveyShortlinkMutation = (
  options?: MutationConfig<typeof fetchSurveyShortlink, Parameters<typeof fetchSurveyShortlink>[0]>
) =>
  useMutation({
    mutationFn: async (queryParams) => fetchSurveyShortlink(queryParams),
    ...options
  })

export const useDeleteSurveyLink = (
  options?: MutationConfig<typeof deleteSurveyLink, Parameters<typeof deleteSurveyLink>[0]>
) =>
  useMutation({
    mutationFn: async (enterpriseSurveyId) => deleteSurveyLink(enterpriseSurveyId),
    ...options
  })

export const usePatchSurveyAccount = (
  surveyId: string | undefined,
  options?: MutationConfig<typeof patchAnonymousSurveyAccount, Parameters<typeof patchAnonymousSurveyAccount>[1]>
) =>
  useMutation({
    mutationFn: (data) => {
      if (!surveyId) {
        throw new Error('Survey id undefined')
      }
      return patchAnonymousSurveyAccount(surveyId, data)
    },
    ...options
  })

export const useFetchSurveys = <T = Awaited<ReturnType<typeof fetchSurveys>>>(
  queryParams: Parameters<typeof fetchSurveys>[0] = {},
  options?: QueryConfig<typeof fetchSurveys, T>
) =>
  useQuery({
    queryKey: surveysQueryKeys.getMany(queryParams),
    queryFn: async () => fetchSurveys(queryParams),
    ...options
  })

export const useFetchEnterpriseSurveys = <T = Awaited<ReturnType<typeof fetchEnterpriseSurveys>>>(
  enterpriseId: string,
  queryParams: Parameters<typeof fetchEnterpriseSurveys>[1] = {},
  options?: QueryConfig<typeof fetchEnterpriseSurveys, T>
) =>
  useQuery({
    queryKey: ['enterprises', enterpriseId, 'surveys', queryParams],
    queryFn: () => fetchEnterpriseSurveys(enterpriseId, queryParams),
    ...options
  })

export const useFetchOneSurvey = (
  surveyId: string,
  queryParams = {},
  options: QueryConfig<typeof fetchOneSurvey> = {}
) =>
  useQuery({
    queryKey: surveysQueryKeys.getOne(surveyId, queryParams),
    queryFn: () => fetchOneSurvey(surveyId, queryParams),
    ...options
  })

export const useCreateSurvey = (options?: MutationConfig<typeof createSurvey, Parameters<typeof createSurvey>[0]>) =>
  useMutation({
    mutationFn: (data) => createSurvey(data),
    ...options
  })

export const usePatchSurvey = (
  options?: MutationConfig<typeof patchSurvey, Parameters<typeof patchSurvey>[1] & { surveyId: string }>
) =>
  useMutation({
    mutationFn: ({ surveyId, ...patchData }) => patchSurvey(surveyId, patchData),
    ...options
  })

export const usePatchOneQuestion = (
  options?: MutationConfig<
    typeof patchOneQuestion,
    Parameters<typeof patchOneQuestion>[2] & { surveyId: string; questionId: string }
  >
) =>
  useMutation({
    mutationFn: ({ surveyId, questionId, ...patchData }) =>
      patchOneQuestion(surveyId, questionId, { ...patchData, id: questionId }),
    ...options
  })

export const useDeleteOneQuestion = (
  options?: MutationConfig<typeof deleteOneQuestion, { surveyId: string; questionId: string }>
) =>
  useMutation({
    mutationFn: ({ surveyId, questionId }) => deleteOneQuestion(surveyId, questionId),
    ...options
  })

export const useCreateManyQuestions = (
  options?: MutationConfig<
    typeof createManyQuestions,
    Parameters<typeof createManyQuestions>[1] & {
      surveyId: string
      queryParams?: Parameters<typeof createManyQuestions>[2]
    }
  >
) =>
  useMutation({
    mutationFn: ({ surveyId, queryParams, ...data }) => createManyQuestions(surveyId, data, queryParams),
    ...options
  })

export const useMoveSurveyPage = (
  surveyId: string,
  options?: MutationConfig<typeof moveSurveyPage, { fromSection: number; body: Parameters<typeof moveSurveyPage>[2] }>
) =>
  useMutation({
    mutationFn: ({ fromSection, body }) => moveSurveyPage(surveyId, fromSection, body),
    ...options
  })

export const useDuplicateSurveyPage = (
  surveyId: string,
  options?: MutationConfig<typeof duplicateSurveyPage, { page: number }>
) =>
  useMutation({
    mutationFn: ({ page }) => duplicateSurveyPage(surveyId, page),
    ...options
  })

export const useDeleteSurveyPage = (
  surveyId: string,
  options?: MutationConfig<typeof deleteSurveyPage, { page: number }>
) =>
  useMutation({
    mutationFn: ({ page }) => deleteSurveyPage(surveyId, page),
    ...options
  })
